"use strict";
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.VoiceOverManager = exports.PlayMode = void 0;
var TweenMaxBase_1 = require("gsap/TweenMaxBase");
var Application_1 = require("../Application");
var Topics = require("../Data/Topics");
var Utils_1 = require("../Utils");
var AudioCategory = require("./AudioCategory");
var HowlerUtils = require("./HowlerUtils");
var BLACK = Utils_1.LogUtils.STYLE_BLACK;
var RED = Utils_1.LogUtils.STYLE_RED_BOLD;
var BLUE = Utils_1.LogUtils.STYLE_BLUE_BOLD;
var PlayMode;
(function (PlayMode) {
    /** Play immediately if nothing else is playing, otherwise add to end of queue */
    PlayMode["Append"] = "append";
    /** Interrupt currently playing and queued VOs. This is the default PlayMode */
    PlayMode["Override"] = "override";
    /** Play immediately if nothing else is playing, otherwise don't play */
    PlayMode["New"] = "new";
})(PlayMode = exports.PlayMode || (exports.PlayMode = {}));
/**
 * Voiceover Manager controls voiceover audio playback and guarantees that only one voiceover will ever be playing at a time
 */
var VoiceOverManager = /** @class */ (function () {
    function VoiceOverManager() {
        var _this = this;
        this.FADE_OUT_DURATION = 150;
        this.debug = false;
        this._queue = [];
        this.log = function (m) {
            var params = [];
            for (var _i = 1; _i < arguments.length; _i++) {
                params[_i - 1] = arguments[_i];
            }
            if (_this.debug) {
                Utils_1.LogUtils.log.apply(Utils_1.LogUtils, __spreadArrays([m, { className: "VoiceOverManager", color: "salmon" }], params));
            }
        };
        this.warn = function (m) {
            var params = [];
            for (var _i = 1; _i < arguments.length; _i++) {
                params[_i - 1] = arguments[_i];
            }
            Utils_1.LogUtils.logWarning.apply(Utils_1.LogUtils, __spreadArrays([m, { className: "VoiceOverManager", color: "salmon" }], params));
        };
        this.error = function (m) {
            var params = [];
            for (var _i = 1; _i < arguments.length; _i++) {
                params[_i - 1] = arguments[_i];
            }
            return Utils_1.LogUtils.logError.apply(Utils_1.LogUtils, __spreadArrays([m, { className: "VoiceOverManager", color: "salmon" }], params));
        };
        // TODO: Pause and unpause are not actually part of the framework
        PubSub.subscribe(Topics.GAME_PAUSED, this.onPause.bind(this));
        PubSub.subscribe(Topics.GAME_UNPAUSED, this.onResume.bind(this));
    }
    Object.defineProperty(VoiceOverManager.prototype, "activeVO", {
        get: function () {
            if (this._queue.length > 0 && this._queue[0].key) {
                return Application_1.Application.instance.audio.getAudioTrack(this._queue[0].key, AudioCategory.VO);
            }
            return undefined;
        },
        enumerable: false,
        configurable: true
    });
    VoiceOverManager.prototype.playVO = function (key, mode, callback) {
        if (!Array.isArray(key)) {
            key = [key];
        }
        var skipCC = false;
        var priority = 0;
        if (typeof mode === "function") {
            callback = mode;
        }
        else if (typeof mode === "object") {
            skipCC = mode.skipCC === true;
            priority = mode.priority || 0;
            callback = mode.callback;
            mode = mode.mode;
        }
        if (!(typeof mode === "string")) {
            mode = PlayMode.Override;
        }
        this.log("🗣 Play VO requested. Key(s): [%c%s%c], Mode: %c%s%c, Priority: %c%s%c", RED, key.join(", "), BLACK, BLUE, mode, BLACK, BLUE, priority, BLACK);
        if (key.length === 1 && this._queue.length === 1 && this._queue[0].key === key[0]) {
            this.warn("🔇 Skipped VO [%c%s%c] because it is already playing", RED, key[0], BLACK);
            if (callback) {
                callback(false);
            }
        }
        else if (this._queue.length === 0 ||
            (mode === PlayMode.Override && priority >= this._queue[0].priority) ||
            (mode === PlayMode.New && priority > this._queue[0].priority)) {
            this.stopVO();
            this.addToQueue(key, callback, skipCC, priority);
            this.playNext();
        }
        else if (mode === PlayMode.Append) {
            this.addToQueue(key, callback, skipCC, priority);
        }
        else if (callback) {
            this.warn("🎟🔇 Firing callback without playing VO(s) %c%s%c", RED, key.join(", "), BLACK);
            callback(false);
        }
        else {
            this.warn("🔇 Skipped VO(s) [%c%s%c] because it is lower priority than what was already playing", RED, key.join(", "), BLACK);
        }
    };
    VoiceOverManager.prototype.stopVO = function () {
        var _this = this;
        var activeVO = this.activeVO;
        var activeItem = this._queue[0];
        if (this._queue.length > 1) {
            this.log("🗑 Clearing VO queue. Length was: %c%s%c", BLUE, this._queue.length, BLACK);
        }
        this._queue.splice(0, this._queue.length);
        if (activeVO) {
            activeVO.off(HowlerUtils.Events.END);
            if (activeVO.getSource().state() === HowlerUtils.State.LOADING) {
                this.warn("🛑 Stopping VO %c%s%c while it is still loading", RED, activeVO.id, BLACK);
                activeVO.stop();
            }
            else if (activeVO.isPlaying()) {
                this.log("🤫 Fading out VO %c%s%c (duration: %c%s%cms)", RED, activeVO.id, BLACK, BLUE, this.FADE_OUT_DURATION, BLACK);
                activeVO.fadeTo(0, this.FADE_OUT_DURATION);
                activeVO.off(HowlerUtils.Events.END);
                activeVO.once(HowlerUtils.Events.FADE, function () {
                    _this.log("😶 Fade out complete %c%s%c", RED, activeVO.id, BLACK);
                    activeVO.stop();
                });
                if (activeItem && !activeItem.skipCC) {
                    // TODO: Captions are not actually part of the framework
                    PubSub.publishSync(Topics.STOP_CAPTION, { id: activeVO.id });
                }
            }
            else {
                activeVO.stop();
            }
            PubSub.publishSync(Topics.VOICEOVER_ENDED, activeVO.id);
        }
        if (this._activeTimeout) {
            this._activeTimeout.kill();
            this._activeTimeout = undefined;
        }
    };
    VoiceOverManager.prototype.addToQueue = function (keys, cb, skipCC, priority) {
        for (var i = 0; i < keys.length; i++) {
            var key = keys[i];
            if (typeof key === "number") {
                this.log("➕ Queueing delay %c%s%c", RED, key, BLACK);
            }
            else {
                this.log("➕ Queueing VO %c%s%c", RED, key, BLACK);
            }
            this._queue.push({
                key: typeof key === "string" ? key : "",
                delay: typeof key === "number" ? key : undefined,
                callback: i === keys.length - 1 ? cb : undefined,
                skipCC: skipCC,
                priority: priority,
            });
        }
        var toLoad = keys.filter(function (it) { return typeof it === "string"; });
        this.log("📂 Loading VO(s) [%c%s%c]", RED, toLoad.join(", "), BLACK);
        Application_1.Application.instance.audio.load(toLoad, AudioCategory.VO);
    };
    VoiceOverManager.prototype.playNext = function () {
        var _this = this;
        if (this._queue.length > 0) {
            var item_1 = this._queue[0];
            if (item_1.delay !== undefined) {
                if (this._activeTimeout) {
                    this._activeTimeout.kill();
                }
                if (this._queue.length === 1) { // skip delay if last item in queue
                    this.log("⌛ Skipping delay because there are no more items in queue");
                    this._activeTimeout = undefined;
                    this._queue.shift();
                    if (item_1.callback) {
                        item_1.callback(true);
                    }
                    this.playNext();
                }
                else {
                    this.log("⏳ Waiting %s seconds before next VO", item_1.delay);
                    this._activeTimeout = TweenMaxBase_1.TweenMax.delayedCall(item_1.delay, function () {
                        _this._activeTimeout = undefined;
                        _this._queue.shift();
                        if (item_1.callback) {
                            item_1.callback(true);
                        }
                        _this.playNext();
                    });
                }
            }
            else {
                var existing = Application_1.Application.instance.audio.getAudioTrack(item_1.key, AudioCategory.VO);
                if (existing !== undefined) {
                    existing.off(HowlerUtils.Events.FADE);
                    if (existing.isPlaying()) {
                        this.warn("🛑 Stopping VO %c%s%c (no fade out)", RED, existing.id, BLACK);
                        existing.stop();
                        PubSub.publishSync(Topics.VOICEOVER_ENDED, item_1.key);
                    }
                }
                else {
                    this.log("📂 Loading VO %c%s%c", RED, item_1.key, BLACK);
                }
                Application_1.Application.instance.audio.load(item_1.key, AudioCategory.VO, function () {
                    if (_this._queue[0] !== item_1) {
                        return;
                    }
                    _this.log("▶️▶️ Playing VO %c%s%c", RED, item_1.key, BLACK);
                    _this.log("ℹ️ Queue length: %c%s%c", BLUE, _this._queue.length, BLACK);
                    Application_1.Application.instance.audio.play(item_1.key, undefined, false, AudioCategory.VO);
                    if (!item_1.skipCC) {
                        // TODO: Captions are not actually part of the framework
                        PubSub.publishSync(Topics.PLAY_CAPTION, { id: item_1.key });
                    }
                    var onEnd = function (pDidPlay) {
                        _this.log("🏁 Completed VO %c%s%c", RED, item_1.key, BLACK);
                        PubSub.publishSync(Topics.VOICEOVER_ENDED, item_1.key);
                        _this._queue.shift();
                        if (_this._activeTimeout) {
                            _this._activeTimeout.kill();
                            _this._activeTimeout = undefined;
                        }
                        _this.playNext();
                        if (item_1.callback) {
                            _this.log("🎟 Firing callback for %c%s%c", RED, item_1.key, BLACK);
                            item_1.callback(pDidPlay);
                        }
                    };
                    if (_this.activeVO) {
                        // TODO: can we keep this decoupled from Howler and only use IAudioTrack?
                        // TODO: Some devices cannot recognize VO duration, the END event is not reliable
                        _this.activeVO.once(HowlerUtils.Events.END, function () { return onEnd(true); });
                        _this.activeVO.once(HowlerUtils.Events.PLAY, function () {
                            PubSub.publishSync(Topics.VOICEOVER_STARTED, item_1.key);
                        });
                    }
                    else {
                        _this.error("⚠️ Vo %c%s%c completed early (did not play?)", RED, item_1.key, BLACK);
                        onEnd(false);
                    }
                });
            }
        }
        else {
            this.log("✅ Nothing left in queue");
        }
    };
    VoiceOverManager.prototype.onPause = function () {
        if (this.activeVO !== undefined && this.activeVO.getTimePos() > 0 && this.activeVO.isPlaying()) {
            this.log("⏸ Pausing VO %c%s%c", RED, this.activeVO.id, BLACK);
            this.activeVO.pause();
            PubSub.publishSync(Topics.VOICEOVER_ENDED, this.activeVO.id);
        }
        if (this._activeTimeout) {
            this._activeTimeout.pause();
        }
    };
    VoiceOverManager.prototype.onResume = function () {
        if (this.activeVO !== undefined && this.activeVO.getTimePos() > 0 && !this.activeVO.isPlaying()) {
            this.log("⏯ Resuming VO %c%s%c", RED, this.activeVO.id, BLACK);
            this.activeVO.play();
            PubSub.publishSync(Topics.VOICEOVER_STARTED, this.activeVO.id);
        }
        if (this._activeTimeout) {
            this._activeTimeout.resume();
        }
    };
    return VoiceOverManager;
}());
exports.VoiceOverManager = VoiceOverManager;
//# sourceMappingURL=VoiceOverManager.js.map